import React from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EventIcon from "@mui/icons-material/Event";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { displayDateRange } from "../../utils/helpers";
import { EcommerceEvent } from "../../models/Event";

type Props = {
    eventList: EcommerceEvent;
    showEventDetails: boolean;
    onToggleEventDetails: () => void;
};

const EventSidebar = ({
    eventList,
    showEventDetails,
    onToggleEventDetails,
}: Props) => {
    const venue = eventList.event?.venueConfiguration?.venue;

    return (
        <Card
            sx={{
                background: (theme) =>
                    theme.palette.mode === "dark"
                        ? "linear-gradient(135deg, rgba(30,30,30,0.9) 0%, rgba(40,40,40,0.95) 100%)"
                        : "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)",
                backdropFilter: "blur(10px)",
                border: (theme) =>
                    theme.palette.mode === "dark"
                        ? "1px solid rgba(255,255,255,0.1)"
                        : "1px solid rgba(0,0,0,0.05)",
                borderRadius: 3,
                boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                        ? "0 8px 32px rgba(0,0,0,0.3)"
                        : "0 8px 32px rgba(0,0,0,0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                            ? "0 12px 40px rgba(0,0,0,0.4)"
                            : "0 12px 40px rgba(0,0,0,0.15)",
                },
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: (theme) => theme.palette.primary.main,
                            borderRadius: "50%",
                            p: 1,
                            mr: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <EventIcon
                            sx={{
                                fontSize: 20,
                                color: "white",
                            }}
                        />
                    </Box>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: "bold",
                            color: (theme) => theme.palette.primary.main,
                            lineHeight: 1.2,
                        }}
                    >
                        {eventList.event?.title}
                    </Typography>
                </Box>
                
                {eventList.event?.showEventDates && (
                    <Box
                        sx={{
                            backgroundColor: (theme) =>
                                `${theme.palette.primary.main}20`,
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                            border: (theme) =>
                                `1px solid ${theme.palette.primary.main}40`,
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                textAlign: "center",
                            }}
                        >
                            {displayDateRange(
                                eventList.event.eventFrom,
                                eventList.event.eventTo,
                                true,
                            )}
                        </Typography>
                    </Box>
                )}
                
                {eventList.event?.details && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <Button
                                onClick={onToggleEventDetails}
                                sx={{
                                    display: {
                                        xs: "flex",
                                        md: "none",
                                    },
                                    gap: 0.5,
                                    backgroundColor: (theme) =>
                                        theme.palette.primary.main,
                                    color: "white",
                                    "&:hover": {
                                        backgroundColor: (theme) =>
                                            theme.palette.primary.dark,
                                    },
                                }}
                                variant="contained"
                                size="small"
                                fullWidth
                            >
                                {showEventDetails
                                    ? "Hide Event Details"
                                    : "Show Event Details"}
                                {showEventDetails ? (
                                    <ExpandLessIcon fontSize="small" />
                                ) : (
                                    <ExpandMoreIcon fontSize="small" />
                                )}
                            </Button>
                        </Box>
                        <Box
                            component="div"
                            sx={{
                                display: {
                                    xs: showEventDetails ? "block" : "none",
                                    md: "block",
                                },
                                mt: 2,
                                p: 2,
                                background: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.05)"
                                        : "rgba(0,0,0,0.02)",
                                borderRadius: 2,
                                border: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "1px solid rgba(255,255,255,0.1)"
                                        : "1px solid rgba(0,0,0,0.05)",
                            }}
                            dangerouslySetInnerHTML={{
                                __html: eventList.event?.details ?? "",
                            }}
                        />
                    </>
                )}
                
                <Divider sx={{ my: 2 }} />
                
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: (theme) => theme.palette.primary.main,
                            borderRadius: "50%",
                            p: 1,
                            mr: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <LocationOnIcon
                            sx={{
                                fontSize: 20,
                                color: "white",
                            }}
                        />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: "bold",
                            color: (theme) => theme.palette.primary.main,
                        }}
                    >
                        Venue
                    </Typography>
                </Box>
                
                <Box
                    sx={{
                        backgroundColor: (theme) =>
                            `${theme.palette.primary.main}20`,
                        borderRadius: 2,
                        p: 2,
                        border: (theme) =>
                            `1px solid ${theme.palette.primary.main}40`,
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            mb: 0.5,
                        }}
                    >
                        {venue?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {venue?.address}, {venue?.city}, {venue?.state}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

export default EventSidebar;